import {
  SMART_LIST_VISIBILITIES,
  type ShareSmartListBody,
  type SmartListChange,
  type SmartListDetail,
  type SmartListVisibility,
  type TeamMember,
  type TeamRow,
} from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useId, useMemo, useState, type FormEvent, type JSX, type ReactNode } from "react";
import { ApiRequestError } from "../api/http";
import { teamQuery } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { Dialog } from "../design/components/Dialog";
import { Icon } from "../design/components/Icon";
import { RadioGroup } from "../design/components/RadioGroup";
import { Select } from "../design/components/Select";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { useLocale, useT, type Translate } from "../i18n/locale";
import type { TabContext } from "../session/tab-context";
import { useShareSmartList } from "./smart-list-queries";
import { describeChanges, type ChangeFields } from "./smart-list-text";
import "./smart-lists.css";

// «Condividi» (piano M3 Task 9, D7 e D14; mock-up `#m-share-dialog` e `openShare`): «Chi la vede» con la spiegazione di
// ogni scelta, i colleghi per una lista condivisa e, per chi modifica tutte le liste (`smart_lists.update: all`),
// «Owner della lista» per darla a un collega. Chi non la gestisce vede chi la vede, senza poterlo cambiare, e chi la
// gestisce. I colleghi sono gli User attivi (come «Crea Smart List»); chi è già fra i destinatari ma non è più un
// collega attivo resta in vista, così non sparisce di nascosto. Senza differenze la finestra si chiude senza scrivere;
// altrimenti l'avviso dice che cosa è cambiato, che resta nello storico.

type Props = {
  tab: TabContext;
  list: SmartListDetail;
  organization: string;
  /** La modifica di tutte le liste: si cambia anche l'owner. */
  canChangeOwner: boolean;
  fields: ChangeFields;
  onReload: () => unknown;
  onClose: () => void;
};

/** Il livello dei Commerciali (User): i soli con cui si condivide una lista, come in «Crea Smart List». */
const COMMERCIAL_LEVEL = 1;

type Field = "recipients" | "owner" | "form";
type Problem = { field: Field; text: (t: Translate) => string };
type Person = { membershipId: string; label: string };

const membersOf = (team: readonly TeamRow[] | undefined): TeamMember[] => (team ?? []).filter((row): row is TeamMember => row.kind === "membership");

/** «sospeso», «archiviato» o «non più attivo», secondo lo stato nel team. */
export function personStatusWord(t: Translate, member: TeamMember | undefined): string {
  if (member?.status === "suspended" || member?.status === "suspending") return t("smartLists.page.personStatus.suspended");
  if (member?.status === "archived") return t("smartLists.page.personStatus.archived");
  return t("smartLists.page.personStatus.inactive");
}

export function ShareDialog({ tab, list, organization, canChangeOwner, fields, onReload, onClose }: Props): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const baseId = useId();
  const formId = `${baseId}-form`;
  const editable = list.permissions.canShare;
  const share = useShareSmartList(tab, list.id);
  const team = useQuery({ ...teamQuery(tab), enabled: editable || !list.owner.active });
  const members = useMemo(() => membersOf(team.data), [team.data]);
  const memberOf = (membershipId: string) => members.find((member) => member.membershipId === membershipId);

  const [visibility, setVisibility] = useState<SmartListVisibility>(list.visibility);
  const [recipients, setRecipients] = useState<ReadonlySet<string>>(() => new Set(list.recipients.map((person) => person.membershipId)));
  const [ownerId, setOwnerId] = useState(list.owner.membershipId);
  const [problem, setProblem] = useState<Problem | null>(null);
  const showOwner = editable && canChangeOwner;
  const nextOwnerId = showOwner ? ownerId : list.owner.membershipId;

  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  const byName = (left: { displayName: string }, right: { displayName: string }) => collator.compare(left.displayName, right.displayName);

  // I colleghi da spuntare: gli User attivi tranne l'owner, poi chi è già fra i destinatari e non lo è più. Chi non la
  // gestisce vede soltanto i destinatari, com'erano.
  let people: Person[];
  if (!editable) {
    people = list.recipients.map((person) => ({
      membershipId: person.membershipId,
      label: person.active ? person.displayName : t("smartLists.page.recipientInactive", { name: person.displayName }),
    }));
  } else {
    const active = members
      .filter((member) => member.status === "active" && member.role.level === COMMERCIAL_LEVEL && member.membershipId !== nextOwnerId)
      .sort(byName)
      .map((member): Person => ({ membershipId: member.membershipId, label: member.displayName }));
    const others = list.recipients
      .filter((person) => person.membershipId !== nextOwnerId && !active.some((candidate) => candidate.membershipId === person.membershipId))
      .map((person): Person => {
        const member = memberOf(person.membershipId);
        const suspended = member?.status === "suspended" || member?.status === "suspending";
        return {
          membershipId: person.membershipId,
          label: t(suspended ? "smartLists.page.recipientSuspended" : "smartLists.page.recipientInactive", { name: person.displayName }),
        };
      });
    people = [...active, ...others];
  }

  // Gli owner possibili: le persone attive dell'azienda; l'owner di adesso resta anche se non è più attivo, con lo stato.
  let owners: Person[] = [{ membershipId: list.owner.membershipId, label: list.owner.displayName }];
  if (team.data) {
    const active = members
      .filter((member) => member.status === "active")
      .sort(byName)
      .map((member): Person => ({ membershipId: member.membershipId, label: member.displayName }));
    const current = active.some((person) => person.membershipId === list.owner.membershipId)
      ? []
      : [{ membershipId: list.owner.membershipId, label: t("smartLists.page.personWithStatus", { name: list.owner.displayName, status: personStatusWord(t, memberOf(list.owner.membershipId)) }) }];
    owners = [...current, ...active];
  }

  const nameOf = (membershipId: string): string =>
    memberOf(membershipId)?.displayName ??
    [list.owner, ...list.recipients].find((person) => person.membershipId === membershipId)?.displayName ??
    membershipId;

  function toggle(membershipId: string, checked: boolean) {
    setRecipients((current) => {
      const next = new Set(current);
      if (checked) next.add(membershipId);
      else next.delete(membershipId);
      return next;
    });
    if (problem?.field === "recipients") setProblem(null);
  }

  /** Il rifiuto del server, sul campo che lo riguarda. */
  function explain(error: unknown) {
    if (error instanceof ApiRequestError) {
      switch (error.code) {
        case "version_conflict":
          void onReload();
          setProblem({ field: "form", text: (tr) => tr("smartLists.page.changedMeanwhile") });
          return;
        case "owner_not_eligible":
          void team.refetch();
          setProblem({ field: "owner", text: (tr) => messageForError(tr, error) });
          return;
        case "smart_list_name_taken": {
          const owner = nameOf(nextOwnerId);
          setProblem({ field: "owner", text: (tr) => tr("smartLists.nameTakenFor", { name: owner }) });
          return;
        }
        case "validation_failed":
        case "recipients_required":
          void team.refetch();
          setProblem({ field: "recipients", text: (tr) => messageForError(tr, error) });
          return;
      }
    }
    setProblem({ field: "form", text: (tr) => messageForError(tr, error) });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editable || share.isPending) return;
    const shown = new Set(people.map((person) => person.membershipId));
    const chosen = visibility === "shared" ? people.filter((person) => recipients.has(person.membershipId) && shown.has(person.membershipId)).map((person) => person.membershipId) : [];
    if (visibility === "shared" && chosen.length === 0) {
      // Chi diventa owner non è anche destinatario: se era il solo spuntato, la finestra lo dice.
      const ownerWasChosen = recipients.has(nextOwnerId) && nextOwnerId !== list.owner.membershipId;
      const owner = nameOf(nextOwnerId);
      setProblem({ field: "recipients", text: (tr) => (ownerWasChosen ? tr("smartLists.page.shareOwnerOnly", { owner }) : tr("smartLists.recipientsRequired")) });
      return;
    }
    const changes: SmartListChange[] = [];
    if (visibility !== list.visibility) changes.push({ kind: "visibility_changed", before: list.visibility, after: visibility });
    const before = list.recipients.map((person) => person.membershipId).sort().join(",");
    if ([...chosen].sort().join(",") !== before) {
      changes.push({
        kind: "recipients_changed",
        before: list.recipients,
        after: chosen.map((membershipId) => ({ membershipId, displayName: nameOf(membershipId) })),
      });
    }
    if (nextOwnerId !== list.owner.membershipId) {
      changes.push({ kind: "owner_changed", before: list.owner, after: { membershipId: nextOwnerId, displayName: nameOf(nextOwnerId) } });
    }
    if (changes.length === 0) {
      onClose();
      return;
    }
    setProblem(null);
    const body: ShareSmartListBody = {
      expectedRevision: list.revision,
      visibility,
      recipientIds: chosen,
      ...(nextOwnerId !== list.owner.membershipId ? { ownerMembershipId: nextOwnerId } : {}),
    };
    try {
      await share.mutateAsync(body);
      toast.show(t("smartLists.page.shared", { changes: describeChanges(t, changes, fields).join(" ") }), "success");
      onClose();
    } catch (error) {
      explain(error);
    }
  }

  const errorOf = (field: Field) => (problem?.field === field ? problem.text(t) : undefined);
  const ownerInactive = editable && !list.owner.active;
  let notice: string | null = null;
  if (!editable) notice = t("smartLists.page.shareReadOnly", { owner: list.owner.displayName });
  else if (ownerInactive) notice = t("smartLists.page.shareOwnerInactive", { owner: list.owner.displayName, status: personStatusWord(t, memberOf(list.owner.membershipId)) });

  let recipientList: ReactNode;
  if (editable && team.isError && !team.data) {
    recipientList = (
      <>
        <p className="g-new-list__note">{messageForError(t, team.error)}</p>
        <Button onClick={() => void team.refetch()}>{t("common.retry")}</Button>
      </>
    );
  } else if (editable && !team.data) {
    recipientList = <p className="g-new-list__note">{t("common.loading")}</p>;
  } else if (people.length === 0) {
    recipientList = <p className="g-new-list__note">{t("smartLists.noColleagues")}</p>;
  } else {
    recipientList = people.map((person) => (
      <Checkbox
        key={person.membershipId}
        id={`${baseId}-recipient-${person.membershipId}`}
        label={person.label}
        checked={recipients.has(person.membershipId)}
        disabled={!editable}
        onChange={(checked) => toggle(person.membershipId, checked)}
      />
    ));
  }

  return (
    <Dialog
      open
      title={t("smartLists.page.shareTitle")}
      onClose={onClose}
      footer={
        editable ? (
          <>
            <Button onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="primary" type="submit" form={formId} disabled={share.isPending} aria-busy={share.isPending ? "true" : undefined}>
              {t("smartLists.page.shareSave")}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>{t("common.close")}</Button>
        )
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void submit(event)} aria-busy={share.isPending} noValidate>
        {notice ? (
          <p className="g-smart-share__notice">
            <Icon name="lock" />
            <span>{notice}</span>
          </p>
        ) : null}
        <RadioGroup
          name={`${baseId}-visibility`}
          legend={t("smartLists.whoSees")}
          options={SMART_LIST_VISIBILITIES.map((option) => ({
            value: option,
            label: t(`smartLists.visibility.${option}`),
            hint: t(`smartLists.page.shareHint.${option}`, { organization }),
          }))}
          value={visibility}
          disabled={!editable}
          onChange={(option) => {
            setVisibility(option);
            if (problem?.field === "recipients") setProblem(null);
          }}
        />

        {visibility === "shared" ? (
          <fieldset className="g-new-list__fieldset">
            <legend>{t("smartLists.shareWith")}</legend>
            {recipientList}
            {errorOf("recipients") ? (
              <p className="g-field__error" role="alert">
                {errorOf("recipients")}
              </p>
            ) : null}
          </fieldset>
        ) : null}

        {showOwner ? (
          <Select
            id={`${baseId}-owner`}
            label={t("smartLists.page.shareOwner")}
            value={ownerId}
            options={owners.map((person) => ({ value: person.membershipId, label: person.label }))}
            hint={t("smartLists.page.shareOwnerNote")}
            error={errorOf("owner")}
            onChange={(value) => {
              setOwnerId(value);
              if (problem?.field === "owner") setProblem(null);
            }}
          />
        ) : null}

        <p className="g-new-list__note">{t("smartLists.page.shareNote")}</p>
        {errorOf("form") ? (
          <p className="g-field__error" role="alert">
            {errorOf("form")}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
